import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import set from 'lodash/set';
import styled from 'styled-components';

import { DefaultEmailDomainConfiguration } from 'src/models/Email/EmailDomain';
import { FieldWrapper, StyledInput } from '@ad-scripts/presentation/components/common/input_field';
import { Row, Section } from 'src/presentation/components/forms/Layout';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import emailAddressesResource from 'src/actions/email/emailAddressesResource';
import EmailDomain from 'src/models/Email/EmailDomain';
import emailDomainsResource from 'src/actions/email/emailDomainsResource';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import InputField from 'src/presentation/components/forms/InputField';

const getDomainsOptions = customerDomains =>
  map(customerDomains, domain => ({
    value: domain.content.customerDomain,
    label: `@${domain.content.customerDomain}`,
  }));

export const useAddEmailAddress = ({
  item = DefaultEmailDomainConfiguration,
  errors,
  onSaveSuccess,
  isEditMode = false,
}) => {
  const dispatch = useDispatch();

  const { update, validate } = emailAddressesResource.effects;
  const { resetErrors } = emailAddressesResource.actions as any;

  const { values: customerDomains = [] } = useResourceValues<EmailDomain>(emailDomainsResource);
  const domainsOptions = getDomainsOptions(customerDomains);

  const formContext = useForm({
    update,
    onSave: ({ workingState }) => {
      const address = `${workingState?.emailLocalPart.toLowerCase()}@${workingState?.emailDomain}`;
      const isValid = !!dispatch(validate(workingState));
      if (isValid) {
        const accountId = find(
          customerDomains,
          domain => get(domain, 'content.customerDomain', '') === workingState.emailDomain
        )?.id;

        onSaveSuccess({ ...workingState, address, accountId });
        clearEmailAddressWorkingState();
      }
    },
    item,
    errors,
    resetErrors,
  });

  const { onSave, updateState } = formContext;

  useEffect(() => {
    const emailDomain = get(item, 'emailDomain', get(customerDomains, '0.content.customerDomain', ''));
    updateState(set({}, 'emailDomain', emailDomain));
  }, [updateState, item, customerDomains]);

  const clearEmailAddressWorkingState = (): void => {
    dispatch(resetErrors());
  };

  const renderEmailDomainForm = (): JSX.Element => (
    <Form context={formContext} preventSaveOnEnter>
      <FullEmailAddressRow>
        <StyledInputField
          className="input-emailLocalPart"
          attr="emailLocalPart"
          data-aid="emailLocalPart"
          label="Email username"
          placeholder="support"
          disabled={isEditMode}
        />
        <Dropdown
          attr="emailDomain"
          data-aid="emailDomain"
          label="Sending domain"
          options={domainsOptions}
          disabled={isEditMode}
        />
      </FullEmailAddressRow>
      <StyledSection>
        <StyledInputField
          attr="senderName"
          data-aid="senderName"
          label="Email sender name"
          description='The name email recipients see (e.g. "Gladly" at support@gladly.com)'
          placeholder="Your company name"
        />
      </StyledSection>
    </Form>
  );

  return { clearEmailAddressWorkingState, onEmailDomainSave: onSave, renderEmailDomainForm };
};

const StyledSection = styled(Section)`
  position: relative;
  width: 100%;

  h3 {
    color: ${p => p.theme.colors.black};
    font-size: ${p => p.theme.fontSize.base};
  }
  margin-bottom: ${p => p.theme.spacing.stackXLarge};
`;
StyledSection.displayName = 'StyledSection';

const StyledInputField = styled(InputField)`
  text-align: left;

  ${FieldWrapper} {
    padding: 0;

    ${StyledInput} {
      padding: 7px 8px;
      border-radius: 4px;
    }
  }
`;

const FullEmailAddressRow = styled(Row)`
  > ${FieldContainer} {
    margin: 0;
  }
`;
FullEmailAddressRow.displayName = 'FullEmailRow';
